import { StrictMode, Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { createRoot } from 'react-dom/client';
import { HashRouter } from 'react-router-dom';
import App from './App';
import './styles/otto-tokens.css';
import './styles/globals.css';
import './styles/report-builder.css';
import './styles/sbs-comparison.css';
import './styles/portal.css';
import './styles/manual-review.css';

/**
 * Global error boundary — catches any React render exceptions that would
 * otherwise unmount the entire app and leave a blank black screen.
 * Shows a minimal recovery UI instead.
 */
class AppErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; errorMessage: string; componentStack: string }
> {
  state = { hasError: false, errorMessage: '', componentStack: '' };

  static getDerivedStateFromError(error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    return { hasError: true, errorMessage: msg };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[AppErrorBoundary] Render crash:', error.message);
    console.error('[AppErrorBoundary] Component stack:', info.componentStack);
    this.setState({ componentStack: info.componentStack ?? '' });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f1117',
          color: '#e5e7eb',
          fontFamily: 'system-ui, sans-serif',
          gap: '16px',
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: '32px' }}>&#x26A0;&#xFE0F;</div>
          <h1 style={{ fontSize: '20px', fontWeight: 600, margin: 0 }}>Something went wrong</h1>
          <p style={{ fontSize: '14px', color: '#9ca3af', maxWidth: '400px', margin: 0 }}>
            Compliance Hub hit an unexpected error. Your session data is safe.
          </p>
          {this.state.errorMessage && (
            <pre style={{
              fontSize: '11px',
              color: '#6b7280',
              background: '#1a1d27',
              padding: '12px',
              borderRadius: '6px',
              maxWidth: '560px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}>
              {this.state.errorMessage}{this.state.componentStack ? '\n\nComponent stack:' + this.state.componentStack.slice(0, 400) : ''}
            </pre>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            Reload Compliance Hub
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AppErrorBoundary>
      <HashRouter>
        <App />
      </HashRouter>
    </AppErrorBoundary>
  </StrictMode>,
);
